/**
 * TemplateVariableSuggestionMenu.jsx
 *
 * A custom suggestion menu for BlockNote that triggers on "{{" and shows
 * categorized template variables with search/filter support.
 *
 * Usage inside <BlockNoteView>:
 *   <TemplateVariableSuggestionMenu editor={editor} />
 *
 * This uses BlockNote's SuggestionMenuController with a custom trigger
 * and a fully custom render for the menu itself.
 */
import { useCallback, useMemo, useState } from "react";
import { SuggestionMenuController } from "@blocknote/react";
import { filterSuggestionItems } from "@blocknote/core/extensions";
import { Box } from "@mui/material";
import {
  ALL_TEMPLATE_VARIABLES,
  TEMPLATE_VARIABLE_CATEGORIES,
} from "./templateVariables";

// ─── Suggestion Item Builder ────────────────────────────────────────
// Builds the array of items BlockNote's SuggestionMenuController needs.
const buildVariableItems = (editor) =>
  ALL_TEMPLATE_VARIABLES.map((v) => ({
    title: v.label,
    // subtitle used for extra search surface — user can type category or key
    subtitle: `${v.categoryLabel} ${v.variable}`,
    group: v.categoryLabel,
    icon: v.categoryIcon,
    variable: v.variable,
    category: v.category,
    onItemClick: () => {
      editor.insertInlineContent([
        {
          type: "templateVariable",
          props: {
            variable: v.variable,
            label: v.label,
          },
        },
        " ", // trailing space so cursor moves past the chip
      ]);
    },
  }));

// ─── Custom Menu Renderer ───────────────────────────────────────────
const TemplateVariableMenu = ({ items, selectedIndex, onItemClick }) => {
  // Group items by their `group` prop
  const grouped = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const group = item.group || "Other";
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(item);
    });
    return map;
  }, [items]);

  // Track flat index for keyboard nav highlighting
  let flatIndex = 0;

  if (items.length === 0) {
    return (
      <Box
        sx={{
          p: 2,
          color: "#999",
          fontSize: "12px",
          fontStyle: "italic",
          background: "#fff",
          borderRadius: "8px",
          border: "1px solid #e0e0e0",
          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        }}
      >
        No matching variables
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        maxHeight: "320px",
        overflowY: "auto",
        minWidth: "260px",
        py: 0.5,
      }}
    >
      {[...grouped.entries()].map(([group, groupItems]) => (
        <Box key={group}>
          {/* Category header */}
          <Box
            sx={{
              px: 1.5,
              pt: 1,
              pb: 0.25,
              fontSize: "10px",
              fontWeight: 700,
              color: "#9e9e9e",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              userSelect: "none",
            }}
          >
            {group}
          </Box>
          {/* Items */}
          {groupItems.map((item) => {
            const idx = flatIndex++;
            const isSelected = idx === selectedIndex;
            return (
              <Box
                key={item.variable}
                onClick={() => onItemClick?.(item)}
                sx={{
                  px: 1.5,
                  py: 0.6,
                  mx: 0.5,
                  cursor: "pointer",
                  borderRadius: "5px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "13px",
                  background: isSelected ? "#f3e8ff" : "transparent",
                  "&:hover": {
                    background: isSelected ? "#f3e8ff" : "#f5f5f5",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: "10px",
                    fontFamily: "'SF Mono', 'Fira Code', monospace",
                    color: "#7e57c2",
                    background: "#ede7f6",
                    borderRadius: "3px",
                    px: 0.5,
                    py: "1px",
                    flexShrink: 0,
                  }}
                >
                  {`{{`}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    color: "#333",
                  }}
                >
                  {item.title}
                </Box>
                <Box
                  component="span"
                  sx={{
                    ml: "auto",
                    fontSize: "10px",
                    fontFamily: "'SF Mono', 'Fira Code', monospace",
                    color: "#bbb",
                  }}
                >
                  {item.variable}
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

// ─── Exported Controller Component ──────────────────────────────────
// Drop this inside <BlockNoteView> alongside the existing mention controller.
const TemplateVariableSuggestionMenu = ({ editor }) => {
  const getItems = useCallback(
    async (query) => {
      const items = buildVariableItems(editor);
      return filterSuggestionItems(items, query);
    },
    [editor],
  );

  return (
    <SuggestionMenuController
      triggerCharacter="{"
      getItems={getItems}
      suggestionMenuComponent={TemplateVariableMenu}
    />
  );
};

export default TemplateVariableSuggestionMenu;
